/**
 * The Vite entry point, replacing Next's app router.
 *
 * THE (dashboard) ROUTE GROUP BECOMES A LAYOUT ROUTE. Next used the parenthesised
 * directory to share a layout across those pages without contributing a URL
 * segment; React Router expresses the same thing as a pathless <Route> whose
 * element wraps an <Outlet />. The paths below therefore drop "(dashboard)"
 * exactly as Next did — /dashboard, not /(dashboard)/dashboard.
 *
 * DashboardLayout is used unmodified. It takes `children`, so passing it an
 * <Outlet /> satisfies both routers without touching the file.
 *
 * `src/app/layout.tsx` becomes <Root>: same providers, same stylesheets. The
 * nonce and the error-tracker key now come from window.__RUNTIME_CONFIG__, which
 * the site templates in per request — see internal/admin.
 */
import { StrictMode, Suspense, lazy } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";

import { Providers } from "@/lib/providers";
import { ErrorTracker } from "@booking/core-ui/errors";
import { runtimeConfig } from "@booking/core-ui/runtime-config";

const DashboardLayout = lazy(() => import("./app/(dashboard)/layout"));
import AuthCallbackPage from "./app/auth/callback/page";
import LoginPage from "./app/login/page";
import HomePage from "./app/page";
import { Loading } from "@booking/core-ui/loading";
const BlogEditorPage = lazy(() => import("./app/(dashboard)/blog/[id]/page"));
const BlogNewPage = lazy(() => import("./app/(dashboard)/blog/new/page"));
const BlogPage = lazy(() => import("./app/(dashboard)/blog/page"));
const BookingsCalendarPage = lazy(() => import("./app/(dashboard)/bookings/calendar/page"));
const BookingsPage = lazy(() => import("./app/(dashboard)/bookings/page"));
const DashboardPage = lazy(() => import("./app/(dashboard)/dashboard/page"));
const LogsPage = lazy(() => import("./app/(dashboard)/logs/page"));
const MailPage = lazy(() => import("./app/(dashboard)/mail/page"));
const MembershipsPage = lazy(() => import("./app/(dashboard)/memberships/page"));
const MessagesPage = lazy(() => import("./app/(dashboard)/messages/page"));
const PlansPage = lazy(() => import("./app/(dashboard)/plans/page"));
const ReschedulesPage = lazy(() => import("./app/(dashboard)/reschedules/page"));
const ServicesPage = lazy(() => import("./app/(dashboard)/services/page"));
const SettingsLinePage = lazy(() => import("./app/(dashboard)/settings/line/page"));
const SettingsMailPage = lazy(() => import("./app/(dashboard)/settings/mail/page"));
const SettingsPage = lazy(() => import("./app/(dashboard)/settings/page"));
const StaffPage = lazy(() => import("./app/(dashboard)/staff/page"));
const UsersIdPage = lazy(() => import("./app/(dashboard)/users/[id]/page"));
const UsersPage = lazy(() => import("./app/(dashboard)/users/page"));

function Root() {
  return (
    <Providers>
      <ErrorTracker nonce={runtimeConfig.nonce} appKey={runtimeConfig.errorTrackerKey} />
      <Suspense fallback={<Loading>…</Loading>}>
      <Routes>
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<HomePage />} />
        <Route element={<DashboardLayout><Outlet /></DashboardLayout>}>
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/new" element={<BlogNewPage />} />
          <Route path="/blog/:id" element={<BlogEditorPage />} />
          <Route path="/bookings/calendar" element={<BookingsCalendarPage />} />
          <Route path="/bookings" element={<BookingsPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/logs" element={<LogsPage />} />
          <Route path="/mail" element={<MailPage />} />
          <Route path="/memberships" element={<MembershipsPage />} />
          <Route path="/messages" element={<MessagesPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/reschedules" element={<ReschedulesPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/settings/line" element={<SettingsLinePage />} />
          <Route path="/settings/mail" element={<SettingsMailPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/users/:id" element={<UsersIdPage />} />
          <Route path="/users" element={<UsersPage />} />
        </Route>
      </Routes>
      </Suspense>
    </Providers>
  );
}

const container = document.getElementById("root");
if (!container) throw new Error("index.html has no #root");

createRoot(container).render(
  <StrictMode>
    <BrowserRouter>
      <Root />
    </BrowserRouter>
  </StrictMode>,
);
